import parse, { DOMNode } from "html-react-parser";

type NodeTextOptions = {
  // The DOM node to get the text content of
  domNode: DOMNode;
  // The attribute to ignore
  ignoreAttrib?: string;
};

/**
 * Return the data we want from the raw report HTML:
 *  - riskStatus ("increased"/"decreased")
 *  - riskCopy ("bring these results..." text)
 *  - arrays of "increasing" / "decreasing" items
 */
export function parseRiskReportData(reportHtml: string) {
  const collected = {
    heading: "" as string | null,
    subHeading: "" as string | null,
    openingText: "" as string | null,
    promptText: "" as string | null,
    status: "" as string | null,
    increasing: [] as string[],
    decreasing: [] as string[],
  };

  const personalCancerIncreasing: string[] = [];
  const familyHistoryIncreasing: string[] = [];
  const otherIncreasing: string[] = [];

  parse(reportHtml, {
    trim: false,
    transform(_reactNode, domNode: DOMNode) {
      if (!domNode || typeof domNode !== "object") return undefined;

      if (domNode.type === "tag") {
        const attribs = domNode.attribs || {};

        // Main heading
        if (attribs["data-risk-copy"] === "heading") {
          collected.heading = getNodeText({ domNode }).trim();
        }

        // Opening text
        if (attribs["data-risk-copy"] === "text") {
          collected.openingText = getNodeText({
            domNode,
            ignoreAttrib: "prompt",
          }).trim();
        }

        // Prompt text
        if (attribs["data-risk-copy"] === "prompt") {
          collected.promptText = getNodeText({ domNode }).trim();
        }

        // Risk status & subheading
        if (attribs["data-risk-status"] === "increased") {
          collected.status = "increased";
          collected.subHeading = getNodeText({ domNode }).trim();
        } else if (attribs["data-risk-status"] === "decreased") {
          collected.status = "decreased";
          collected.subHeading = getNodeText({ domNode }).trim();
        } else if (attribs["data-risk-status"] === "average") {
          collected.status = "average";
          collected.subHeading = getNodeText({ domNode }).trim();
        } else if (attribs["data-risk-status"] === "n/a") {
          collected.status = "n/a";
        }

        // Risk item (increasing/decreasing)
        const riskItemVal = attribs["data-risk-item"];

        // Risk type only for personal cancer and family history
        const riskTypeVal = attribs["data-risk-type"];

        // Risk items in correct order (personal cancer, family history, other)
        if (riskItemVal === "increasing") {
          const text = getNodeText({ domNode }).trim();

          if (riskTypeVal === "personal-cancer") {
            personalCancerIncreasing.push(text);
          } else if (riskTypeVal === "family-history") {
            familyHistoryIncreasing.push(text);
          } else {
            otherIncreasing.push(text);
          }
        } else if (riskItemVal === "decreasing") {
          collected.decreasing.push(getNodeText({ domNode }).trim());
        }
      }
      return undefined;
    },
  });

  collected.increasing = [
    ...personalCancerIncreasing,
    ...familyHistoryIncreasing,
    ...otherIncreasing,
  ];

  return collected;
}

// Grab the text content of a node, ignoring any tags.
function getNodeText({ domNode, ignoreAttrib = "" }: NodeTextOptions): string {
  let text = "";
  const stack = [domNode];

  while (stack.length > 0) {
    const node = stack.pop();

    if (node?.type === "text") {
      text += node.data || "";
    } else if (
      node?.type === "tag" ||
      node?.type === "script" ||
      node?.type === "style"
    ) {
      if (Array.isArray(node.children)) {
        for (const child of node.children) {
          const attribs = Object.values(node.attribs || {});
          const ignore = attribs.includes(ignoreAttrib);

          if (!ignore) {
            stack.push(child as DOMNode);
          }
        }
      }
    }
  }

  return text;
}
